/**
 * Contrato do módulo **Relatórios** (`MOD_REPORTS`, #664): catálogo de
 * relatórios, janela de período, pedido, job da fila e o documento de blocos
 * que o e-mail e o PDF desenham.
 *
 * Origem: #664 — 2026-10-07 12:54 +0200.
 *
 * Por quê: os 22 relatórios entregáveis (R1–R21 e o R25, registro de
 * checklists, #336; o R22 virou tela na #843, o R23 é formato e o R24 é regra)
 * compartilham o mesmo pedido, a mesma fila e a
 * mesma entrega. Um catálogo único aqui faz web, Android, iOS e Functions
 * concordarem sobre o que existe, em que grupo aparece e se o seletor de
 * período se aplica — sem quatro listas para divergir.
 *
 * Contrato:
 *  - `resolveReportWindow` é pura e determinística: mesmo `nowMs` + fuso ⇒
 *    mesma janela. Lança `ReportWindowError` com o motivo, nunca devolve janela
 *    inválida. Quem chama no servidor converte o erro em `invalid-argument`.
 *  - O cliente manda o **preset**; só `CUSTOM` manda datas. A janela é sempre
 *    resolvida **no servidor**, no `tenant.timeZone` (#664 §3.2/§3.3) — o
 *    relógio do aparelho nunca decide o período.
 *
 * Usado por: callable `requestOperationsReport` e trigger `onReportJobCreated`
 * (`apps/functions/src/reports/`); tela Relatórios no web (`apps/app`),
 * Android e iOS (via contrato gerado). Consumidores entram nas etapas 3–7 da
 * #664.
 *
 * Arquitetura: tempo = server > tenant TZ; teto de 30 dias validado no
 * servidor (o cliente valida também, só por UX). O documento de blocos
 * carrega **texto já resolvido no idioma do destinatário** e números crus com
 * o tipo de formatação — o renderizador formata número e data no locale, mas
 * nunca traduz dado (#664 §9).
 *
 * Dados: ver `RequestOperationsReportInputSchema`, `ReportJobSchema` e
 * `ReportDocumentSchema` abaixo, cada um com exemplo.
 *
 * Falhas conhecidas (herdadas, #664 §5):
 *  - quantidade de unidades diferentes nunca soma — por isso `QTY` sempre
 *    carrega `unit` e não existe bloco de total em quantidade;
 *  - `PeriodPresetSchema` (`period.ts`) é do BI do admin, com presets e fuso
 *    fixos diferentes; não reutilizar aqui.
 *
 * Dicas: mexeu em enum ou schema daqui? `npm run contract && npm run
 * contract:swift` no mesmo commit — o `contract:check` reprova sem isso.
 *
 * Issues: #664 (módulo), #843 (R22 busca global), #336 (R25, registro de
 * checklists — substitui o export CSV), #308/#327 (relatório
 * mensal removido, cujo núcleo inspirou a fila).
 */
import { z } from 'zod';
import { Sku } from './enums';
import { ymdInTimeZone } from './monthlyWaste';
import { LabelIdSchema, TenantIdSchema } from './schemas';
import { startOfDayInTz } from './wallTime';

// ---------------------------------------------------------------------------
// Catálogo
// ---------------------------------------------------------------------------

/**
 * Relatórios que podem ser pedidos. O comentário de cada um aponta o número
 * da especificação na #664 (comentários A–G).
 */
export const ReportId = {
  /** R1 — Visão Geral (A). */
  OVERVIEW: 'OVERVIEW',
  /** R2 — Rastreabilidade de Baixas (B). */
  DROP_TRACEABILITY: 'DROP_TRACEABILITY',
  /** R3 — Auditoria de Etiqueta (D). Sem período: é a etiqueta inteira. */
  LABEL_AUDIT: 'LABEL_AUDIT',
  /** R4 — Baixas Parciais (B). */
  PARTIAL_DROPS: 'PARTIAL_DROPS',
  /** R5 — Perdas (C). */
  LOSSES: 'LOSSES',
  /** R6 — Por Produto (E). */
  BY_PRODUCT: 'BY_PRODUCT',
  /** R7 — Por Responsável (F). */
  BY_RESPONSIBLE: 'BY_RESPONSIBLE',
  /** R8 — Por Usuário (F). */
  BY_USER: 'BY_USER',
  /** R9 — Responsável × Usuário da Baixa (F). */
  RESPONSIBLE_BY_USER: 'RESPONSIBLE_BY_USER',
  /** R10 — Validade (E). Metade estado atual, metade período. */
  SHELF_LIFE: 'SHELF_LIFE',
  /** R11 — Estoque (E). Sem período: foto do momento do pedido. */
  STOCK: 'STOCK',
  /** R12 — Ciclo da Etiqueta (D). */
  LABEL_LIFECYCLE: 'LABEL_LIFECYCLE',
  /** R13 — Produtos com Muitas Manipulações (D). */
  HIGH_HANDLING_PRODUCTS: 'HIGH_HANDLING_PRODUCTS',
  /** R14 — Consumo Antes do Descarte (C). */
  CONSUMED_BEFORE_DISCARD: 'CONSUMED_BEFORE_DISCARD',
  /** R15 — Consumo Antes do Vencimento (C). */
  CONSUMED_BEFORE_EXPIRY: 'CONSUMED_BEFORE_EXPIRY',
  /** R16 — Status MIXED (D). */
  MIXED_STATUS: 'MIXED_STATUS',
  /** R17 — Estornos (B). */
  REVERSALS: 'REVERSALS',
  /** R18 — Movimentos por Horário (G). */
  MOVEMENTS_BY_HOUR: 'MOVEMENTS_BY_HOUR',
  /** R19 — Por Dia da Semana (G). */
  BY_WEEKDAY: 'BY_WEEKDAY',
  /** R20 — Atividade Operacional (G). */
  OPERATIONAL_ACTIVITY: 'OPERATIONAL_ACTIVITY',
  /** R21 — Executivo Mensal (A). Só mês-calendário. */
  EXECUTIVE_MONTHLY: 'EXECUTIVE_MONTHLY',
  /**
   * R25 — Registro de Checklists (#336, 2026-10-20 22:28 +0200). Substitui o
   * export CSV do histórico: o registro sanitário sai em PDF por e-mail. Exige
   * `MOD_REPORTS` **e** `MOD_CHECKLISTS` (callable e `processReportJob`).
   */
  CHECKLIST_RECORDS: 'CHECKLIST_RECORDS',
} as const;
export type ReportId = (typeof ReportId)[keyof typeof ReportId];
export const ALL_REPORT_IDS = Object.values(ReportId) as [ReportId, ...ReportId[]];
export const ReportIdSchema = z.enum(ALL_REPORT_IDS);

/**
 * Seções da área (§28 do enunciado, confirmado no comentário G da #664).
 *
 * O §28 não posiciona R10, R19 e R20. Decisão de 2026-10-07: R10 (Validade)
 * vai com Estoque — os dois leem as etiquetas vigentes e respondem "o que eu
 * tenho" —, e R19/R20 formam a seção Atividade, ao lado de R18, que o §28
 * deixou em Baixas.
 */
export const ReportGroup = {
  OVERVIEW: 'OVERVIEW',
  DROPS: 'DROPS',
  LOSSES: 'LOSSES',
  LABELS: 'LABELS',
  PRODUCTS: 'PRODUCTS',
  PEOPLE: 'PEOPLE',
  STOCK: 'STOCK',
  ACTIVITY: 'ACTIVITY',
  EXECUTIVE: 'EXECUTIVE',
  /** Registro de checklists (R25, #336): só aparece com `MOD_CHECKLISTS`. */
  CHECKLISTS: 'CHECKLISTS',
} as const;
export type ReportGroup = (typeof ReportGroup)[keyof typeof ReportGroup];
export const ALL_REPORT_GROUPS = Object.values(ReportGroup) as [ReportGroup, ...ReportGroup[]];
export const ReportGroupSchema = z.enum(ALL_REPORT_GROUPS);

/**
 * Como o seletor de período se aplica a um relatório.
 *  - `WINDOW`: todos os presets, `CUSTOM` até 30 dias.
 *  - `NONE`: sem período (R3 é a etiqueta inteira; R11 é foto do instante).
 *  - `PARTIAL`: metade do relatório ignora o período (R10) — o pedido leva a
 *    janela para a metade histórica, e o relatório rotula as duas.
 *  - `MONTH`: só "Este mês" e "Mês anterior" (R21).
 */
export const ReportPeriodMode = {
  WINDOW: 'WINDOW',
  NONE: 'NONE',
  PARTIAL: 'PARTIAL',
  MONTH: 'MONTH',
} as const;
export type ReportPeriodMode = (typeof ReportPeriodMode)[keyof typeof ReportPeriodMode];
export const ALL_REPORT_PERIOD_MODES = Object.values(ReportPeriodMode) as [
  ReportPeriodMode,
  ...ReportPeriodMode[],
];

/** Uma linha do catálogo. `specRef` é o número da especificação na #664. */
export type ReportCatalogEntry = {
  readonly id: ReportId;
  readonly specRef: `R${number}`;
  readonly group: ReportGroup;
  readonly periodMode: ReportPeriodMode;
};

/**
 * Catálogo canônico, **na ordem de exibição** dentro de cada grupo. É a fonte
 * que as três plataformas leem (Android e iOS pelo contrato gerado).
 */
export const REPORT_CATALOG: readonly ReportCatalogEntry[] = [
  { id: ReportId.OVERVIEW, specRef: 'R1', group: ReportGroup.OVERVIEW, periodMode: ReportPeriodMode.WINDOW },
  { id: ReportId.DROP_TRACEABILITY, specRef: 'R2', group: ReportGroup.DROPS, periodMode: ReportPeriodMode.WINDOW },
  { id: ReportId.PARTIAL_DROPS, specRef: 'R4', group: ReportGroup.DROPS, periodMode: ReportPeriodMode.WINDOW },
  { id: ReportId.REVERSALS, specRef: 'R17', group: ReportGroup.DROPS, periodMode: ReportPeriodMode.WINDOW },
  { id: ReportId.LOSSES, specRef: 'R5', group: ReportGroup.LOSSES, periodMode: ReportPeriodMode.WINDOW },
  {
    id: ReportId.CONSUMED_BEFORE_DISCARD,
    specRef: 'R14',
    group: ReportGroup.LOSSES,
    periodMode: ReportPeriodMode.WINDOW,
  },
  {
    id: ReportId.CONSUMED_BEFORE_EXPIRY,
    specRef: 'R15',
    group: ReportGroup.LOSSES,
    periodMode: ReportPeriodMode.WINDOW,
  },
  { id: ReportId.LABEL_AUDIT, specRef: 'R3', group: ReportGroup.LABELS, periodMode: ReportPeriodMode.NONE },
  { id: ReportId.LABEL_LIFECYCLE, specRef: 'R12', group: ReportGroup.LABELS, periodMode: ReportPeriodMode.WINDOW },
  { id: ReportId.MIXED_STATUS, specRef: 'R16', group: ReportGroup.LABELS, periodMode: ReportPeriodMode.WINDOW },
  {
    id: ReportId.HIGH_HANDLING_PRODUCTS,
    specRef: 'R13',
    group: ReportGroup.LABELS,
    periodMode: ReportPeriodMode.WINDOW,
  },
  { id: ReportId.BY_PRODUCT, specRef: 'R6', group: ReportGroup.PRODUCTS, periodMode: ReportPeriodMode.WINDOW },
  { id: ReportId.BY_RESPONSIBLE, specRef: 'R7', group: ReportGroup.PEOPLE, periodMode: ReportPeriodMode.WINDOW },
  { id: ReportId.BY_USER, specRef: 'R8', group: ReportGroup.PEOPLE, periodMode: ReportPeriodMode.WINDOW },
  { id: ReportId.RESPONSIBLE_BY_USER, specRef: 'R9', group: ReportGroup.PEOPLE, periodMode: ReportPeriodMode.WINDOW },
  { id: ReportId.STOCK, specRef: 'R11', group: ReportGroup.STOCK, periodMode: ReportPeriodMode.NONE },
  { id: ReportId.SHELF_LIFE, specRef: 'R10', group: ReportGroup.STOCK, periodMode: ReportPeriodMode.PARTIAL },
  { id: ReportId.MOVEMENTS_BY_HOUR, specRef: 'R18', group: ReportGroup.ACTIVITY, periodMode: ReportPeriodMode.WINDOW },
  { id: ReportId.BY_WEEKDAY, specRef: 'R19', group: ReportGroup.ACTIVITY, periodMode: ReportPeriodMode.WINDOW },
  {
    id: ReportId.OPERATIONAL_ACTIVITY,
    specRef: 'R20',
    group: ReportGroup.ACTIVITY,
    periodMode: ReportPeriodMode.WINDOW,
  },
  {
    id: ReportId.EXECUTIVE_MONTHLY,
    specRef: 'R21',
    group: ReportGroup.EXECUTIVE,
    periodMode: ReportPeriodMode.MONTH,
  },
  {
    id: ReportId.CHECKLIST_RECORDS,
    specRef: 'R25',
    group: ReportGroup.CHECKLISTS,
    periodMode: ReportPeriodMode.WINDOW,
  },
];

/** Módulo extra que um relatório exige além do `MOD_REPORTS` (#336); `null` = nenhum. */
export function reportExtraModuleSku(id: ReportId): string | null {
  return id === ReportId.CHECKLIST_RECORDS ? Sku.MOD_CHECKLISTS : null;
}

/** Linha do catálogo de um relatório. Lança se o catálogo não cobre o id (bug de build). */
export function reportCatalogEntry(id: ReportId): ReportCatalogEntry {
  const entry = REPORT_CATALOG.find((e) => e.id === id);
  if (!entry) throw new Error(`REPORT_CATALOG não cobre ${id}`);
  return entry;
}

// ---------------------------------------------------------------------------
// Período
// ---------------------------------------------------------------------------

/**
 * Presets do seletor (#664, enunciado). Enum **novo**: o `PeriodPresetSchema`
 * do admin tem outros presets e fuso fixo em São Paulo (#664 §3.2).
 */
export const ReportPeriodPreset = {
  TODAY: 'TODAY',
  YESTERDAY: 'YESTERDAY',
  THIS_WEEK: 'THIS_WEEK',
  THIS_MONTH: 'THIS_MONTH',
  LAST_MONTH: 'LAST_MONTH',
  CUSTOM: 'CUSTOM',
} as const;
export type ReportPeriodPreset = (typeof ReportPeriodPreset)[keyof typeof ReportPeriodPreset];
export const ALL_REPORT_PERIOD_PRESETS = Object.values(ReportPeriodPreset) as [
  ReportPeriodPreset,
  ...ReportPeriodPreset[],
];
export const ReportPeriodPresetSchema = z.enum(ALL_REPORT_PERIOD_PRESETS);

/** Presets aceitos por relatório `MONTH` (R21). */
export const MONTH_ONLY_PRESETS: readonly ReportPeriodPreset[] = [
  ReportPeriodPreset.THIS_MONTH,
  ReportPeriodPreset.LAST_MONTH,
];

/**
 * Teto da janela, em dias-calendário, **inclusive** nas duas pontas (owner,
 * enunciado da #664). Vale para todo preset: "Este mês" e "Mês anterior" têm
 * no máximo 31 dias e por isso são isentos — o teto é do `CUSTOM`, que é o
 * único que o usuário escolhe livremente (§3.2: o teto é o que substitui a
 * proteção de custo que o relatório mensal tinha).
 */
export const MAX_CUSTOM_RANGE_DAYS = 30;

/** `YYYY-MM-DD`. */
export const YmdSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

/**
 * Período pedido. Exemplo:
 *   { "preset": "CUSTOM", "from": "2026-09-01", "to": "2026-09-15" }
 *   { "preset": "THIS_WEEK" }
 */
export const ReportPeriodInputSchema = z
  .object({
    preset: ReportPeriodPresetSchema,
    from: YmdSchema.optional(),
    to: YmdSchema.optional(),
  })
  .refine(
    (p) => (p.preset === ReportPeriodPreset.CUSTOM) === (p.from !== undefined && p.to !== undefined),
    { message: 'from/to vão só (e sempre) com o preset CUSTOM.' },
  );
export type ReportPeriodInput = z.infer<typeof ReportPeriodInputSchema>;

/**
 * Janela resolvida, no fuso do tenant.
 *  - `fromYmd`/`toYmd`: dias **inclusivos**, ids de `daily_ops`.
 *  - `startMs`/`endMs`: `[startMs, endMs)` em epoch ms, para `activity_log`.
 */
export type ReportWindow = {
  preset: ReportPeriodPreset;
  fromYmd: string;
  toYmd: string;
  startMs: number;
  endMs: number;
  days: number;
  timeZone: string;
};

export const ReportWindowErrorReason = {
  INVALID_DATE: 'INVALID_DATE',
  FROM_AFTER_TO: 'FROM_AFTER_TO',
  RANGE_TOO_LONG: 'RANGE_TOO_LONG',
  FUTURE: 'FUTURE',
  PRESET_NOT_ALLOWED: 'PRESET_NOT_ALLOWED',
} as const;
export type ReportWindowErrorReason =
  (typeof ReportWindowErrorReason)[keyof typeof ReportWindowErrorReason];

/** Período recusado; `reason` vai para o `details` do `HttpsError`. */
export class ReportWindowError extends Error {
  constructor(readonly reason: ReportWindowErrorReason) {
    super(`Período inválido: ${reason}`);
    this.name = 'ReportWindowError';
  }
}

const DAY_MS = 86_400_000;

/** Dias entre duas datas `YYYY-MM-DD`, em aritmética UTC pura (DST-safe). */
function ymdToUtcDay(ymd: string): number {
  return Date.UTC(Number(ymd.slice(0, 4)), Number(ymd.slice(5, 7)) - 1, Number(ymd.slice(8, 10))) / DAY_MS;
}

function shiftYmd(ymd: string, days: number): string {
  return new Date((ymdToUtcDay(ymd) + days) * DAY_MS).toISOString().slice(0, 10);
}

/** A data existe no calendário (rejeita `2026-02-30`, que o regex aceita). */
function isRealDate(ymd: string): boolean {
  return YmdSchema.safeParse(ymd).success && shiftYmd(ymd, 0) === ymd;
}

/** Segunda-feira da semana de `ymd` — a semana dos filtros termina domingo (`wallTime.ts`). */
function mondayOf(ymd: string): string {
  const dow = new Date(ymdToUtcDay(ymd) * DAY_MS).getUTCDay(); // 0 = domingo
  return shiftYmd(ymd, -((dow + 6) % 7));
}

function firstOfMonth(ymd: string): string {
  return `${ymd.slice(0, 7)}-01`;
}

function lastOfMonth(ymd: string): string {
  const y = Number(ymd.slice(0, 4));
  const m = Number(ymd.slice(5, 7));
  return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
}

/**
 * Resolve o período pedido numa janela concreta, no fuso do tenant.
 *
 * "Hoje", "Esta semana" e "Este mês" terminam **hoje** (dia em curso
 * incluído), nunca no fim da semana/mês: o dado do futuro não existe, e a
 * janela até o fim do mês inflaria `days` e as médias por dia.
 *
 * `CUSTOM`: as duas pontas são inclusivas, `from <= to`, `to` não pode ser
 * depois de hoje, e `to - from + 1 <= 30`. Tudo isso é validado **aqui**, no
 * servidor — a UI repete a regra só para não deixar o usuário enviar.
 *
 * Fuso inválido: `ymdInTimeZone` cai em UTC e `startOfDayInTz` devolve `null`;
 * nesse caso a função usa meia-noite UTC. Tenant com fuso inválido é bug de
 * dado que o callable registra no log; o relatório sai, com a janela em UTC.
 */
export function resolveReportWindow(input: {
  period: ReportPeriodInput;
  periodMode: ReportPeriodMode;
  timeZone: string;
  nowMs: number;
}): ReportWindow {
  const { period, periodMode, timeZone, nowMs } = input;
  if (periodMode === ReportPeriodMode.MONTH && !MONTH_ONLY_PRESETS.includes(period.preset)) {
    throw new ReportWindowError(ReportWindowErrorReason.PRESET_NOT_ALLOWED);
  }
  const today = ymdInTimeZone(nowMs, timeZone);
  let fromYmd: string;
  let toYmd: string;
  switch (period.preset) {
    case ReportPeriodPreset.TODAY:
      fromYmd = today;
      toYmd = today;
      break;
    case ReportPeriodPreset.YESTERDAY:
      fromYmd = shiftYmd(today, -1);
      toYmd = fromYmd;
      break;
    case ReportPeriodPreset.THIS_WEEK:
      fromYmd = mondayOf(today);
      toYmd = today;
      break;
    case ReportPeriodPreset.THIS_MONTH:
      fromYmd = firstOfMonth(today);
      toYmd = today;
      break;
    case ReportPeriodPreset.LAST_MONTH: {
      const lastDayPrev = shiftYmd(firstOfMonth(today), -1);
      fromYmd = firstOfMonth(lastDayPrev);
      toYmd = lastOfMonth(lastDayPrev);
      break;
    }
    case ReportPeriodPreset.CUSTOM: {
      const from = period.from ?? '';
      const to = period.to ?? '';
      if (!isRealDate(from) || !isRealDate(to)) {
        throw new ReportWindowError(ReportWindowErrorReason.INVALID_DATE);
      }
      if (from > to) throw new ReportWindowError(ReportWindowErrorReason.FROM_AFTER_TO);
      if (to > today) throw new ReportWindowError(ReportWindowErrorReason.FUTURE);
      if (ymdToUtcDay(to) - ymdToUtcDay(from) + 1 > MAX_CUSTOM_RANGE_DAYS) {
        throw new ReportWindowError(ReportWindowErrorReason.RANGE_TOO_LONG);
      }
      fromYmd = from;
      toYmd = to;
      break;
    }
  }
  const startMs = startOfDayInTz(fromYmd, timeZone) ?? ymdToUtcDay(fromYmd) * DAY_MS;
  const nextDay = shiftYmd(toYmd, 1);
  const endMs = startOfDayInTz(nextDay, timeZone) ?? ymdToUtcDay(nextDay) * DAY_MS;
  return {
    preset: period.preset,
    fromYmd,
    toYmd,
    startMs,
    endMs,
    days: ymdToUtcDay(toYmd) - ymdToUtcDay(fromYmd) + 1,
    timeZone,
  };
}

/**
 * Período de comparação: o intervalo **imediatamente anterior de mesma
 * duração**, no fuso do tenant (comentário A da #664, tabela "Comparação com
 * o período anterior"):
 *  - Hoje → ontem; Ontem → anteontem;
 *  - Esta semana (segunda → hoje) → a semana anterior **inteira**;
 *  - Este mês (dia 1 → hoje) → o mês anterior **inteiro**;
 *  - Mês anterior → o mês antes dele;
 *  - Personalizado de N dias → os N dias antes de `from`.
 */
export function previousReportWindow(window: ReportWindow): ReportWindow {
  const tz = window.timeZone;
  let fromYmd: string;
  let toYmd: string;
  switch (window.preset) {
    case ReportPeriodPreset.THIS_WEEK:
      fromYmd = shiftYmd(window.fromYmd, -7);
      toYmd = shiftYmd(window.fromYmd, -1);
      break;
    case ReportPeriodPreset.THIS_MONTH:
    case ReportPeriodPreset.LAST_MONTH: {
      const lastDayPrev = shiftYmd(window.fromYmd, -1);
      fromYmd = firstOfMonth(lastDayPrev);
      toYmd = lastOfMonth(lastDayPrev);
      break;
    }
    case ReportPeriodPreset.TODAY:
    case ReportPeriodPreset.YESTERDAY:
    case ReportPeriodPreset.CUSTOM:
      toYmd = shiftYmd(window.fromYmd, -1);
      fromYmd = shiftYmd(window.fromYmd, -window.days);
      break;
  }
  const startMs = startOfDayInTz(fromYmd, tz) ?? ymdToUtcDay(fromYmd) * DAY_MS;
  const nextDay = shiftYmd(toYmd, 1);
  const endMs = startOfDayInTz(nextDay, tz) ?? ymdToUtcDay(nextDay) * DAY_MS;
  return {
    preset: window.preset,
    fromYmd,
    toYmd,
    startMs,
    endMs,
    days: ymdToUtcDay(toYmd) - ymdToUtcDay(fromYmd) + 1,
    timeZone: tz,
  };
}

// ---------------------------------------------------------------------------
// Pedido (callable `requestOperationsReport`)
// ---------------------------------------------------------------------------

/**
 * Input do callable. Exemplos:
 *   { "tenantId": "BR-1", "reportId": "LOSSES", "period": { "preset": "LAST_MONTH" } }
 *   { "tenantId": "BR-1", "reportId": "LABEL_AUDIT", "labelId": "lbl_000000" }
 *   { "tenantId": "BR-1", "reportId": "STOCK" }
 *
 * `period` é obrigatório nos relatórios `WINDOW`/`PARTIAL`/`MONTH` e proibido
 * nos `NONE`; `labelId` só no R3, que é obrigado a tê-lo. O código curto da
 * etiqueta é resolvido para `labelId` **no cliente**, com desambiguação por
 * período, porque o `shortCode` é reutilizado depois da baixa (#664 §5.7).
 */
export const RequestOperationsReportInputSchema = z
  .object({
    tenantId: TenantIdSchema,
    reportId: ReportIdSchema,
    period: ReportPeriodInputSchema.optional(),
    // Auditoria 2026-10-07 (#664, servidor M4): `labelId` com `/` virava
    // caminho de subcoleção no `db.doc(...)`, lançava a cada reentrega e
    // prendia o job. Ids de etiqueta são do Firestore: sem barra.
    labelId: LabelIdSchema.refine((s) => /^[A-Za-z0-9_-]{1,128}$/.test(s), {
      message: 'Identificador de etiqueta inválido.',
    }).optional(),
  })
  .superRefine((input, ctx) => {
    const mode = reportCatalogEntry(input.reportId).periodMode;
    const needsPeriod = mode !== ReportPeriodMode.NONE;
    if (needsPeriod !== (input.period !== undefined)) {
      ctx.addIssue({
        code: 'custom',
        path: ['period'],
        message: needsPeriod ? 'Este relatório exige período.' : 'Este relatório não aceita período.',
      });
    }
    const needsLabel = input.reportId === ReportId.LABEL_AUDIT;
    if (needsLabel !== (input.labelId !== undefined)) {
      ctx.addIssue({
        code: 'custom',
        path: ['labelId'],
        message: needsLabel ? 'A auditoria exige a etiqueta.' : 'Só a auditoria aceita etiqueta.',
      });
    }
  });
export type RequestOperationsReportInput = z.infer<typeof RequestOperationsReportInputSchema>;

// ---------------------------------------------------------------------------
// Job da fila — `tenants/{tenantId}/reportJobs/{jobId}`
// ---------------------------------------------------------------------------

type Brand<K, T> = K & { readonly __brand: T };
export type ReportJobId = Brand<string, 'ReportJobId'>;
export const ReportJobIdSchema = z
  .string()
  .min(1)
  .transform((s) => s as ReportJobId)
  .meta({ type: 'string', minLength: 1 });

/**
 * Ciclo de vida do job. `QUEUED → RUNNING → SENT | NO_DATA | FAILED |
 * CANCELLED`. Os quatro últimos são terminais.
 *  - `NO_DATA`: a janela não tem dado; nada é enviado.
 *  - `CANCELLED`: o tenant perdeu o `MOD_REPORTS` entre o pedido e a geração
 *    (a reentrega pode durar dias) — nada é enviado.
 */
export const ReportJobStatus = {
  QUEUED: 'QUEUED',
  RUNNING: 'RUNNING',
  SENT: 'SENT',
  NO_DATA: 'NO_DATA',
  FAILED: 'FAILED',
  CANCELLED: 'CANCELLED',
} as const;
export type ReportJobStatus = (typeof ReportJobStatus)[keyof typeof ReportJobStatus];
export const ALL_REPORT_JOB_STATUSES = Object.values(ReportJobStatus) as [
  ReportJobStatus,
  ...ReportJobStatus[],
];
export const ReportJobStatusSchema = z.enum(ALL_REPORT_JOB_STATUSES);

/** Status que ainda vão mudar — um pedido igual devolve o job existente. */
export const OPEN_REPORT_JOB_STATUSES: readonly ReportJobStatus[] = [
  ReportJobStatus.QUEUED,
  ReportJobStatus.RUNNING,
];

/**
 * Depois deste tempo sem atualização, um job aberto (`QUEUED`/`RUNNING`) é
 * tratado como abandonado: o trigger desiste dele (`FAILED`) e o callable não
 * o usa para deduplicar.
 *
 * Auditoria 2026-10-07 18:30 +0200 (#664, servidor A2): o desistir valia 24 h,
 * igual à janela de reentrega das Functions de 2ª geração — a entrega que
 * fecharia o job nunca chegava, e um job preso bloqueava o mesmo pedido
 * (mesma `dedupeKey`) para sempre. 2 h cobre várias reentregas de um erro
 * passageiro.
 */
export const OPEN_REPORT_JOB_STALE_AFTER_MS = 2 * 3_600_000;

/** Motivo de um job `FAILED`/`CANCELLED`. */
export const ReportJobReason = {
  MODULE_INACTIVE: 'MODULE_INACTIVE',
  INVALID_DATA: 'INVALID_DATA',
  LABEL_NOT_FOUND: 'LABEL_NOT_FOUND',
  RENDER_FAILED: 'RENDER_FAILED',
  SEND_FAILED: 'SEND_FAILED',
  // Auditoria 2026-10-07 (#664, servidor B3): a pessoa deixou de ser
  // OWNER/MANAGER (ou membro) entre o pedido e a geração — o relatório leva
  // nome de colaborador e não pode sair. Status `CANCELLED`.
  ACCESS_REVOKED: 'ACCESS_REVOKED',
  // Auditoria 2026-10-07 (#664, servidor B1): o destinatário está na lista de
  // bloqueio (`emailBlocklist`); antes o job fechava `SENT` sem nada sair.
  RECIPIENT_BLOCKED: 'RECIPIENT_BLOCKED',
} as const;

/**
 * Motivos de `FAILED` que podem passar numa nova tentativa — só neles as telas
 * oferecem "Pedir de novo". Nos demais, reenviar o mesmo pedido falha de novo
 * e gasta a cota de pedidos (auditoria 2026-10-07, #664, web B2).
 */
export const RETRYABLE_REPORT_JOB_REASONS: readonly ReportJobReason[] = [
  ReportJobReason.RENDER_FAILED,
  ReportJobReason.SEND_FAILED,
];
export type ReportJobReason = (typeof ReportJobReason)[keyof typeof ReportJobReason];
export const ALL_REPORT_JOB_REASONS = Object.values(ReportJobReason) as [
  ReportJobReason,
  ...ReportJobReason[],
];

/**
 * Documento do job. **Escrito só pelo servidor** (callable cria, trigger
 * avança); OWNER/MANAGER leem para a lista da tela. Exemplo:
 *   {
 *     "tenantId": "BR-1", "reportId": "LOSSES",
 *     "dedupeKey": "LOSSES|uid-1|2026-09-01|2026-09-30",
 *     "requestedByUid": "uid-1", "recipientEmail": "gerente@exemplo.com",
 *     "locale": "pt-BR",
 *     "period": { "preset": "LAST_MONTH" },
 *     "window": { "fromYmd": "2026-09-01", "toYmd": "2026-09-30", "timeZone": "America/Sao_Paulo" },
 *     "labelId": null,
 *     "status": "SENT", "reason": null,
 *     "createdAtMillis": 1791240000000, "updatedAtMillis": 1791240009000,
 *     "emailSentAtMillis": 1791240009000,
 *     "pdfBytes": 184320, "durationMs": 8400
 *   }
 * Datas em epoch ms de servidor (`Date.now()` da Function), nunca do aparelho.
 */
export const ReportJobSchema = z.object({
  tenantId: TenantIdSchema,
  reportId: ReportIdSchema,
  /**
   * Mesmo relatório + pessoa + janela (ou etiqueta, ou dia da foto). Um pedido
   * com a mesma chave e job aberto (`QUEUED`/`RUNNING`) devolve o job
   * existente. Job terminado não bloqueia: pedir de novo gera job novo, e a
   * lista da tela mostra os dois. Índice `(dedupeKey, status)`.
   */
  dedupeKey: z.string().min(1),
  requestedByUid: z.string().min(1),
  /** E-mail autenticado de quem pediu, no instante do pedido. Nunca digitado. */
  recipientEmail: z.string().min(3),
  locale: z.string().min(2),
  period: ReportPeriodInputSchema.nullable(),
  window: z
    .object({ fromYmd: YmdSchema, toYmd: YmdSchema, timeZone: z.string().min(1) })
    .nullable(),
  labelId: z.string().nullable(),
  status: ReportJobStatusSchema,
  reason: z.enum(ALL_REPORT_JOB_REASONS).nullable(),
  createdAtMillis: z.number().int().nonnegative(),
  updatedAtMillis: z.number().int().nonnegative(),
  /** Guarda de reentrega: presente ⇒ o e-mail já saiu, nunca reenviar. */
  emailSentAtMillis: z.number().int().nonnegative().nullable(),
  /** Observabilidade (#664 §15): tamanho do PDF e duração da geração. */
  pdfBytes: z.number().int().nonnegative().nullable(),
  durationMs: z.number().int().nonnegative().nullable(),
});
export type ReportJob = z.infer<typeof ReportJobSchema>;

/**
 * Output do callable. `deduplicated: true` quando já havia um job aberto para
 * o mesmo relatório, pessoa e janela — devolvido em vez de criar outro.
 */
export const RequestOperationsReportOutputSchema = z.object({
  jobId: z.string().min(1),
  status: ReportJobStatusSchema,
  deduplicated: z.boolean(),
});
export type RequestOperationsReportOutput = z.infer<typeof RequestOperationsReportOutputSchema>;

// ---------------------------------------------------------------------------
// Documento de blocos — o que o e-mail e o PDF desenham
// ---------------------------------------------------------------------------

/**
 * Como um número é formatado pelo renderizador, no locale do destinatário.
 *  - `INT`: contagem de etiquetas/movimentos.
 *  - `PCT`: percentual já em 0–100 (não razão), 1 casa.
 *  - `QTY`: quantidade, **sempre** com `unit` — unidades não somam (#664 §5.1).
 *  - `DATETIME`/`DATE`: epoch ms, formatado no fuso do tenant.
 *  - `DURATION`: milissegundos, formatado como "2 d 4 h".
 */
export const ReportValueFormat = {
  INT: 'INT',
  PCT: 'PCT',
  QTY: 'QTY',
  DATETIME: 'DATETIME',
  DATE: 'DATE',
  DURATION: 'DURATION',
  TEXT: 'TEXT',
} as const;
export type ReportValueFormat = (typeof ReportValueFormat)[keyof typeof ReportValueFormat];
export const ALL_REPORT_VALUE_FORMATS = Object.values(ReportValueFormat) as [
  ReportValueFormat,
  ...ReportValueFormat[],
];

/**
 * Uma célula. `null` aparece como "—" e significa "não existe" (ex.: % sem
 * denominador) — nunca é igual a zero, que leria como "não se perdeu nada".
 */
export const ReportCellSchema = z.object({
  format: z.enum(ALL_REPORT_VALUE_FORMATS),
  value: z.union([z.number(), z.string()]).nullable(),
  /** Unidade de `QTY` (`UnitOfMeasure`), já traduzida pelo servidor. */
  unit: z.string().optional(),
  /** Âncora interna do PDF para onde a célula leva (drill-down, R24). */
  linkTo: z.string().optional(),
});
export type ReportCell = z.infer<typeof ReportCellSchema>;

export const ReportBlockKind = {
  KPIS: 'KPIS',
  TABLE: 'TABLE',
  NOTICE: 'NOTICE',
  TEXT: 'TEXT',
} as const;
export type ReportBlockKind = (typeof ReportBlockKind)[keyof typeof ReportBlockKind];

/**
 * Bloco do documento. Todo texto (título, rótulo, cabeçalho) chega **já no
 * idioma do destinatário**, resolvido pelo servidor; o renderizador só
 * formata número e data. Exemplo de tabela:
 *   {
 *     "kind": "TABLE", "anchor": "products", "title": "Perdas por produto",
 *     "columns": ["Produto", "Descartado", "Vencido"],
 *     "rows": [[{ "format": "TEXT", "value": "Frango desfiado", "linkTo": "product-p1" },
 *               { "format": "QTY", "value": 4.5, "unit": "kg" },
 *               { "format": "QTY", "value": 8, "unit": "kg" }]],
 *     "emailRowLimit": 10, "truncatedRows": 0
 *   }
 */
export const ReportBlockSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal(ReportBlockKind.KPIS),
    anchor: z.string().optional(),
    title: z.string().optional(),
    items: z.array(z.object({ label: z.string().min(1), cell: ReportCellSchema, note: z.string().optional() })),
  }),
  z.object({
    kind: z.literal(ReportBlockKind.TABLE),
    anchor: z.string().optional(),
    title: z.string().min(1),
    columns: z.array(z.string()).min(1),
    rows: z.array(z.array(ReportCellSchema)),
    /** Linhas que entram no corpo do e-mail; o PDF leva todas. */
    emailRowLimit: z.number().int().positive(),
    /**
     * Índices das colunas que o corpo do e-mail mostra (600 px). Ausente =
     * as primeiras. Auditoria 2026-10-07 (#664, servidor M2): o corte
     * posicional escondia a perda por produto no e-mail do R5.
     */
    emailColumns: z.array(z.number().int().nonnegative()).optional(),
    /** Linhas cortadas do PDF pelo teto de tamanho (#664, ~7 MB) — 0 normalmente. */
    truncatedRows: z.number().int().nonnegative(),
  }),
  z.object({
    kind: z.literal(ReportBlockKind.NOTICE),
    /** `INFO` (ex.: retenção de 30 dias) ou `WARNING` (ex.: detalhe cortado). */
    level: z.enum(['INFO', 'WARNING']),
    text: z.string().min(1),
  }),
  z.object({
    kind: z.literal(ReportBlockKind.TEXT),
    anchor: z.string().optional(),
    title: z.string().optional(),
    paragraphs: z.array(z.string().min(1)).min(1),
  }),
]);
export type ReportBlock = z.infer<typeof ReportBlockSchema>;

/**
 * Seção do documento. `inEmail: false` ⇒ só no PDF (o detalhe até o
 * movimento); o e-mail leva o resumo e os top-N.
 */
export const ReportSectionSchema = z.object({
  anchor: z.string().min(1),
  title: z.string().min(1),
  inEmail: z.boolean(),
  blocks: z.array(ReportBlockSchema),
});
export type ReportSection = z.infer<typeof ReportSectionSchema>;

/**
 * O relatório pronto para renderizar. Montado por um builder por relatório
 * (`apps/functions/src/reports/operations/`), consumido pelo template de
 * e-mail `REPORT_OPERATIONS` e pelo renderizador de PDF.
 */
export const ReportDocumentSchema = z.object({
  reportId: ReportIdSchema,
  /** Título do relatório no idioma do destinatário. */
  title: z.string().min(1),
  tenantName: z.string().min(1),
  /** Linha de período já escrita ("1 a 30 de setembro de 2026"), ou foto ("situação em …"). */
  periodLabel: z.string().min(1),
  timeZone: z.string().min(1),
  generatedAtMillis: z.number().int().nonnegative(),
  /** Rodapé: relógio usado (event time) e exclusões declaradas (#664 §5.6, §5.10). */
  footnotes: z.array(z.string()),
  sections: z.array(ReportSectionSchema).min(1),
});
export type ReportDocument = z.infer<typeof ReportDocumentSchema>;

/**
 * Chave de deduplicação de um pedido (ver `ReportJobSchema.dedupeKey`). Sem
 * período, a chave usa a etiqueta (R3) ou o dia da foto no fuso do tenant
 * (R11): duas fotos do estoque no mesmo dia são o mesmo pedido enquanto a
 * primeira não terminou.
 */
export function reportDedupeKey(input: {
  reportId: ReportId;
  uid: string;
  window: Pick<ReportWindow, 'fromYmd' | 'toYmd'> | null;
  labelId: string | null;
  snapshotYmd: string;
}): string {
  const scope = input.window
    ? `${input.window.fromYmd}|${input.window.toYmd}`
    : (input.labelId ?? `snapshot|${input.snapshotYmd}`);
  return `${input.reportId}|${input.uid}|${scope}`;
}
